import type { ObjectiveCondition, ObjectivesDiff } from 'helpers/objectives-identity';
import { ConditionChips } from '../SavedTargeting/ConditionChips';

type RowProps = { label: string; children: React.ReactNode };

const Row = ({ label, children }: RowProps) => (
  <div className="grid grid-cols-[6rem_1fr] items-start gap-3">
    <span className="supporting pt-0.5">{label}</span>
    {children}
  </div>
);

type DiffRowsProps = { diff: ObjectivesDiff; showKeeps: boolean };

/** Removes, Adds and (when asked) Keeps, each only if it has something in it, then a reorder note. */
export const ObjectivesDiffRows = ({ diff, showKeeps }: DiffRowsProps) => (
  <div className="flex flex-col gap-2">
    {diff.removes.length ? (
      <Row label="Removes">
        <ConditionChips conditions={labelled(diff.removes)} tone="remove" />
      </Row>
    ) : null}
    {diff.adds.length ? (
      <Row label="Adds">
        <ConditionChips conditions={labelled(diff.adds)} tone="add" />
      </Row>
    ) : null}
    {showKeeps && diff.keeps.length ? (
      <Row label="Keeps">
        <ConditionChips conditions={labelled(diff.keeps)} />
      </Row>
    ) : null}
    {diff.reordered ? <p className="supporting">Same objectives, with the secondaries in a different order.</p> : null}
  </div>
);

type RoleRowsProps = { conditions: readonly ObjectiveCondition[] };

/** The primary on one row and the secondaries on another: what a save will store. */
export const RoleRows = ({ conditions }: RoleRowsProps) => {
  const primary = conditions.filter(({ role }) => role === 'primary');
  const secondary = conditions.filter(({ role }) => role === 'secondary');
  return (
    <div className="flex flex-col gap-2">
      <Row label="Primary">
        <ConditionChips conditions={primary} />
      </Row>
      <Row label="Secondary">
        {secondary.length ? <ConditionChips conditions={secondary} /> : <span className="supporting">None</span>}
      </Row>
    </div>
  );
};

/** In a diff, primary and secondary chips sit together, so the primary says which it is. */
const labelled = (conditions: readonly ObjectiveCondition[]) =>
  conditions.map(({ role, key, label }) => ({ key, label: role === 'primary' ? `Primary: ${label}` : label }));
